import { useEffect, useRef } from 'react';
import type { AppDownloadModalProps } from '../components/AppDownloadModal';
import { DAY_MS } from '../utils/appAdoptionStorage';
import { devicePlatform, isInNativeApp } from '../utils/deviceUtils';
import { isAppAdoptionAudience } from '../utils/appAdoptionAudience';
import { calendarDay } from '../utils/interventionDate';
import { useAdoptionPrompt } from './useAdoptionPrompt';
import { APP_MODAL_STORE_LINKS } from '../constants/appAdoption';
import { WEB_CUTOFF_DATE } from '../config';

const HIDDEN_UNTIL_KEY = 'app-modal-hidden-until';
const HIDE_MS = 2 * DAY_MS;

interface AppDownloadModalInput {
  ready: boolean;
  email: string | null;
  serviceDeskId: number | null;
  hasMobileApp: boolean;
  isImpersonating: boolean;
  role: string;
  serviceDeskName: string;
  cutoffDate?: string;
}

/** Local-midnight cutoff, or null when the value is not exactly a real calendar day (2026-02-31). */
const parseCutoff = (value: string): Date | null =>
  calendarDay(value) === value ? new Date(`${value}T00:00:00`) : null;

const isBeforeCutoff = (cutoff: Date): boolean => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()) < cutoff;
};

export const useAppDownloadModal = ({
  ready,
  email,
  serviceDeskId,
  hasMobileApp,
  isImpersonating,
  role,
  serviceDeskName,
  cutoffDate = WEB_CUTOFF_DATE,
}: AppDownloadModalInput): AppDownloadModalProps | null => {
  const platform = devicePlatform();
  const { dismissed, trackPrompt, act, dismiss } = useAdoptionPrompt({
    hiddenKey: HIDDEN_UNTIL_KEY,
    actHideMs: HIDE_MS,
    dismissHideMs: HIDE_MS,
    platform,
    role,
    serviceDeskName,
  });
  const shownRef = useRef(false);
  const cutoff = parseCutoff(cutoffDate);

  const visible = ready
    && !dismissed
    && cutoff !== null
    && isBeforeCutoff(cutoff)
    && isAppAdoptionAudience({ platform, inNativeApp: isInNativeApp(), email, serviceDeskId, hasMobileApp, isImpersonating });

  useEffect(() => {
    if (!visible || shownRef.current) return;
    shownRef.current = true;
    trackPrompt('App Download Modal Shown');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount, props read at that moment
  }, [visible]);

  // `!cutoff` and `desktop` only narrow the types: `visible` already excludes both
  if (!visible || !cutoff || platform === 'desktop') return null;

  return {
    cutoff,
    storeUrl: APP_MODAL_STORE_LINKS[platform],
    onOpenStore: act('App Download Modal Click'),
    onContact: act('App Download Modal Contact'),
    onDismiss: dismiss('App Download Modal Dismiss'),
  };
};
